import maplibregl, { type GeoJSONSource, type LngLatBoundsLike, type Map as MlMap } from 'maplibre-gl'
import { Crosshair, Maximize2, Minus, Plus } from 'lucide-react'
import { useEffect, useMemo, useRef } from 'react'
import type { LngLat, NetworkResponse } from '../api'
import { NODE_COLOR, NODE_KIND_SHORT, TIER_COLOR, TIER_WIDTH } from '../format'
import type { CableTier } from '../api'

export type Selection = { type: 'node' | 'cable'; id: string } | null

export interface MapHighlight {
  cableIds?: string[]
  nodeIds?: string[]
  point?: { lngLat: LngLat; radiusM: number; label: string }
  /** Colour intent: trace paths are accent, outages are danger. */
  tone?: 'accent' | 'danger'
}

/** Map click handling while adding or moving plant: clicks go here instead of selecting. */
export interface MapInteraction {
  hint: string
  onClick: (e: { lngLat: LngLat; nodeId?: string }) => void
  /** Always-visible buttons in the instruction bar (Cancel, Undo point…): touch screens have no Esc key. */
  actions?: { label: string; onClick: () => void }[]
}

/** Geometry being drawn (new cable route, new site position). */
export interface MapDraft {
  line?: LngLat[]
  points?: LngLat[]
}

interface Props {
  interaction?: MapInteraction
  draft?: MapDraft
  network: NetworkResponse
  theme: 'light' | 'dark'
  selection: Selection
  highlight: MapHighlight
  tiers: Record<CableTier, boolean>
  issueNodeIds: Set<string>
  focus: { lngLat: LngLat; zoom?: number; bounds?: [LngLat, LngLat]; seq: number } | null
  onSelect: (s: Selection) => void
}

const OSM_TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'

function cssVar(name: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || '#888'
}

function bounds(network: NetworkResponse): LngLatBoundsLike | null {
  const pts = [...network.nodes.map((n) => n.location), ...network.cables.flatMap((c) => c.path)]
  if (!pts.length) return null
  let [w, s, e, n] = [180, 90, -180, -90]
  for (const [x, y] of pts) {
    w = Math.min(w, x)
    e = Math.max(e, x)
    s = Math.min(s, y)
    n = Math.max(n, y)
  }
  return [w, s, e, n]
}

/** Keep fitted plant clear of the legend (bottom-left) and zoom controls (right) where there is room. */
function fitPadding(map: MlMap) {
  const { clientWidth: w, clientHeight: h } = map.getContainer()
  const roomy = w > 700 && h > 450
  return roomy ? { top: 40, bottom: 40, left: 270, right: 70 } : { top: 40, bottom: 40, left: 30, right: 60 }
}

/** Metres-per-pixel at zoom 0 for 512 px tiles, used to draw the OTDR uncertainty circle to scale. */
function metresToPixelsAtZoom0(m: number, lat: number) {
  return m / ((40_075_016.686 * Math.cos((lat * Math.PI) / 180)) / 512)
}

export function MapView({ network, theme, selection, highlight, tiers, issueNodeIds, focus, onSelect, interaction, draft }: Props) {
  const container = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MlMap | null>(null)
  const readyRef = useRef(false)
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const interactionRef = useRef(interaction)
  interactionRef.current = interaction

  const cablesGeo = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: network.cables.map((c) => ({
        type: 'Feature' as const,
        id: c.id,
        properties: { id: c.id, name: c.name, tier: c.tier, planned: c.status === 'planned', label: `${c.name} · ${c.fiberCount}F ${c.tier}` },
        geometry: { type: 'LineString' as const, coordinates: c.path },
      })),
    }),
    [network],
  )
  const nodesGeo = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: network.nodes.map((n) => ({
        type: 'Feature' as const,
        properties: { id: n.id, name: n.name, kind: n.kind, issue: issueNodeIds.has(n.id), label: `${n.name} · ${NODE_KIND_SHORT[n.kind]}` },
        geometry: { type: 'Point' as const, coordinates: n.location },
      })),
    }),
    [network, issueNodeIds],
  )

  // ---- create the map once
  useEffect(() => {
    if (!container.current) return
    const map = new maplibregl.Map({
      container: container.current,
      style: {
        version: 8,
        sources: {
          osm: { type: 'raster', tiles: [OSM_TILES], tileSize: 256, maxzoom: 19, attribution: '© OpenStreetMap contributors' },
        },
        layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
      },
      center: [121.15, 14.74],
      zoom: 14,
      attributionControl: { compact: true },
      dragRotate: false,
      pitchWithRotate: false,
    })
    map.touchZoomRotate.disableRotation()
    mapRef.current = map
    // Debug handle: always in dev, and on demand with ?fnmsdebug so a support session can inspect
    // the live map without a special build.
    if (import.meta.env.DEV || location.search.includes('fnmsdebug')) (window as unknown as { __fnmsMap?: MlMap }).__fnmsMap = map
    map.on('error', (e) => console.error('[fnms map]', e.error?.message ?? e))

    map.on('load', () => {
      map.addSource('cables', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
      map.addSource('nodes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
      map.addSource('hl-cables', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
      map.addSource('hl-nodes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
      map.addSource('pin', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
      map.addSource('selected', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
      map.addSource('draft', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })

      const tierColor = ['match', ['get', 'tier'], ...Object.entries(TIER_COLOR).flat(), '#888'] as unknown as maplibregl.ExpressionSpecification
      const tierWidth = ['match', ['get', 'tier'], ...Object.entries(TIER_WIDTH).flat(), 3] as unknown as maplibregl.ExpressionSpecification
      const scaled = (w: maplibregl.ExpressionSpecification, extra = 0) =>
        ['interpolate', ['linear'], ['zoom'], 12, ['*', 0.5, ['+', w, extra]], 16, ['+', w, extra], 19, ['*', 1.6, ['+', w, extra]]] as unknown as maplibregl.ExpressionSpecification

      map.addLayer({ id: 'cables-casing', type: 'line', source: 'cables', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#ffffff', 'line-width': scaled(tierWidth, 2.5) } })
      map.addLayer({
        id: 'cables',
        type: 'line',
        source: 'cables',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': tierColor, 'line-width': scaled(tierWidth) },
      })
      // Highlighted cables sit above the plant so a broken cable is the boldest thing on the map.
      map.addLayer({ id: 'hl-cables', type: 'line', source: 'hl-cables', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#1d4ed8', 'line-width': scaled(tierWidth, 6), 'line-opacity': 0.5 } })
      map.addLayer({ id: 'selected-line', type: 'line', source: 'selected', filter: ['==', ['geometry-type'], 'LineString'], paint: { 'line-color': '#facc15', 'line-width': scaled(tierWidth, 3), 'line-opacity': 0.9 } })
      map.addLayer({
        id: 'pin-radius',
        type: 'circle',
        source: 'pin',
        paint: { 'circle-color': '#dc2626', 'circle-opacity': 0.22, 'circle-stroke-color': '#dc2626', 'circle-stroke-width': 2.5, 'circle-stroke-opacity': 0.9, 'circle-radius': 0, 'circle-pitch-alignment': 'map' },
      })
      map.addLayer({
        id: 'hl-nodes',
        type: 'circle',
        source: 'hl-nodes',
        paint: { 'circle-color': 'rgba(0,0,0,0)', 'circle-stroke-color': '#1d4ed8', 'circle-stroke-width': 4, 'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 8, 17, 16] },
      })
      map.addLayer({
        id: 'nodes',
        type: 'circle',
        source: 'nodes',
        paint: {
          'circle-color': ['match', ['get', 'kind'], ...Object.entries(NODE_COLOR).flat(), '#666'] as unknown as maplibregl.ExpressionSpecification,
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            11, ['match', ['get', 'kind'], 'pop', 6, 'lcp', 2.5, 'closure', 3, 1.2],
            14, ['match', ['get', 'kind'], 'pop', 8, 'lcp', 5, 'closure', 5, 3],
            17, ['match', ['get', 'kind'], 'pop', 11, 'lcp', 10, 'closure', 9, 7.5],
          ] as unknown as maplibregl.ExpressionSpecification,
          'circle-stroke-color': ['case', ['get', 'issue'], '#dc2626', '#ffffff'] as unknown as maplibregl.ExpressionSpecification,
          'circle-stroke-width': [
            'interpolate', ['linear'], ['zoom'],
            11, ['case', ['get', 'issue'], 1.5, 0.5],
            15, ['case', ['get', 'issue'], 3, 2],
          ] as unknown as maplibregl.ExpressionSpecification,
        },
      })
      map.addLayer({ id: 'selected-node', type: 'circle', source: 'selected', filter: ['==', ['geometry-type'], 'Point'], paint: { 'circle-color': 'rgba(0,0,0,0)', 'circle-stroke-color': '#facc15', 'circle-stroke-width': 4, 'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 9, 17, 17] } })
      map.addLayer({ id: 'draft-line', type: 'line', source: 'draft', filter: ['==', ['geometry-type'], 'LineString'], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#7c3aed', 'line-width': 4, 'line-dasharray': [2, 1.5] } })
      map.addLayer({ id: 'draft-points', type: 'circle', source: 'draft', filter: ['==', ['geometry-type'], 'Point'], paint: { 'circle-color': '#7c3aed', 'circle-radius': 6, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2 } })
      map.addLayer({ id: 'pin', type: 'circle', source: 'pin', paint: { 'circle-color': '#dc2626', 'circle-radius': 7, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 3 } })

      // Hover names (no glyph server needed) and click selection. Nodes win over cables.
      const popup = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 12 })
      const hit = (e: maplibregl.MapMouseEvent) => {
        const box: [maplibregl.PointLike, maplibregl.PointLike] = [
          [e.point.x - 6, e.point.y - 6],
          [e.point.x + 6, e.point.y + 6],
        ]
        const node = map.queryRenderedFeatures(box, { layers: ['nodes'] })[0]
        if (node) return { type: 'node' as const, f: node }
        const cable = map.queryRenderedFeatures(box, { layers: ['cables'] })[0]
        if (cable) return { type: 'cable' as const, f: cable }
        return null
      }
      map.on('mousemove', (e) => {
        const h = hit(e)
        map.getCanvas().style.cursor = interactionRef.current ? 'crosshair' : h ? 'pointer' : ''
        if (h) popup.setLngLat(e.lngLat).setText(String(h.f.properties.label)).addTo(map)
        else popup.remove()
      })
      map.on('mouseout', () => popup.remove())
      map.on('click', (e) => {
        const h = hit(e)
        if (interactionRef.current) {
          interactionRef.current.onClick({ lngLat: [e.lngLat.lng, e.lngLat.lat], nodeId: h?.type === 'node' ? String(h.f.properties.id) : undefined })
          return
        }
        onSelectRef.current(h ? { type: h.type, id: String(h.f.properties.id) } : null)
      })
      readyRef.current = true
      map.fire('fnms:ready')
    })
    return () => {
      readyRef.current = false
      map.remove()
      mapRef.current = null
    }
  }, [])

  /** Run `fn` now if the map has loaded, otherwise once it has. */
  const whenReady = (fn: (map: MlMap) => void) => {
    const map = mapRef.current
    if (!map) return
    if (readyRef.current) fn(map)
    else map.once('fnms:ready' as never, () => fn(map))
  }

  // ---- data
  useEffect(() => {
    whenReady((map) => {
      ;(map.getSource('cables') as GeoJSONSource).setData(cablesGeo)
      ;(map.getSource('nodes') as GeoJSONSource).setData(nodesGeo)
    })
  }, [cablesGeo, nodesGeo])

  // ---- node name labels at street zoom. HTML markers avoid needing a glyph (font) server; only
  // nodes inside the viewport are labelled, capped so dense areas stay readable and fast.
  useEffect(() => {
    const markers = new Map<string, maplibregl.Marker>()
    const LABEL_MIN_ZOOM = 16.5
    const MAX_LABELS = 150
    const update = () => {
      const map = mapRef.current
      if (!map) return
      const want = new Set<string>()
      if (map.getZoom() >= LABEL_MIN_ZOOM) {
        const b = map.getBounds()
        // Simple collision avoidance: skip a label whose box would overlap one already placed.
        // POPs, closures and LCPs are placed before NAPs so the more important names win.
        const rank: Record<string, number> = { pop: 0, closure: 1, lcp: 2, nap: 3 }
        const boxes: [number, number, number, number][] = []
        const inView = network.nodes.filter((n) => b.contains(n.location)).sort((x, y) => (rank[x.kind] ?? 9) - (rank[y.kind] ?? 9))
        for (const n of inView) {
          if (want.size >= MAX_LABELS) break
          const pt = map.project(n.location)
          const w = 7 * n.name.replace(/^DEMO-(?:[A-Z]{2}-)?/, '').length + 12
          const box: [number, number, number, number] = [pt.x + 8, pt.y - 10, pt.x + 8 + w, pt.y + 10]
          if (boxes.some((o) => box[0] < o[2] && box[2] > o[0] && box[1] < o[3] && box[3] > o[1])) continue
          boxes.push(box)
          want.add(n.id)
        }
      }
      for (const [id, m] of markers) if (!want.has(id)) (m.remove(), markers.delete(id))
      for (const id of want) {
        if (markers.has(id)) continue
        const n = network.nodes.find((x) => x.id === id)!
        const el = document.createElement('div')
        el.className = 'fnms-label'
        el.textContent = n.name.replace(/^DEMO-(?:[A-Z]{2}-)?/, '')
        el.title = n.name
        el.setAttribute('aria-hidden', 'true')
        markers.set(id, new maplibregl.Marker({ element: el, anchor: 'left', offset: [10, 0] }).setLngLat(n.location).addTo(map))
      }
    }
    whenReady((map) => {
      map.on('moveend', update)
      update()
    })
    return () => {
      mapRef.current?.off('moveend', update)
      for (const m of markers.values()) m.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [network])

  // Fit to the network the first time it arrives.
  const fitted = useRef(false)
  const fit = (auto = false) =>
    whenReady((map) => {
      if (auto && fitted.current) return
      const b = bounds(network)
      if (b) map.fitBounds(b, { padding: fitPadding(map), maxZoom: 17, duration: fitted.current ? 600 : 0 })
      fitted.current = true
    })
  useEffect(() => {
    if (!fitted.current) fit(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [network])

  // ---- theme: dim the basemap in dark mode so plant colours stay readable
  useEffect(() => {
    whenReady((map) => {
      const dark = theme === 'dark'
      // Dark: dim and desaturate the basemap so plant colours lead, but keep enough brightness for
      // street names to stay readable at night. Light: a touch of desaturation for the same reason.
      map.setPaintProperty('osm', 'raster-saturation', dark ? -0.7 : -0.35)
      map.setPaintProperty('osm', 'raster-brightness-max', dark ? 0.55 : 1)
      map.setPaintProperty('osm', 'raster-contrast', dark ? 0.08 : 0)
      const casing = cssVar('--map-casing')
      map.setPaintProperty('cables-casing', 'line-color', casing)
      // Node outlines stay white in both themes so dark markers (POP) remain visible on a dark map.
      map.setPaintProperty('pin', 'circle-stroke-color', casing)
    })
  }, [theme])

  // ---- tier visibility
  useEffect(() => {
    whenReady((map) => {
      const on = (Object.keys(tiers) as CableTier[]).filter((t) => tiers[t])
      const filter: maplibregl.FilterSpecification = ['in', ['get', 'tier'], ['literal', on]]
      map.setFilter('cables', filter)
      map.setFilter('cables-casing', filter)
    })
  }, [tiers])

  // ---- highlight + selection
  useEffect(() => {
    whenReady((map) => {
      const color = highlight.tone === 'danger' ? '#dc2626' : '#1d4ed8'
      const cableSet = new Set(highlight.cableIds ?? [])
      ;(map.getSource('hl-cables') as GeoJSONSource).setData({ type: 'FeatureCollection', features: cablesGeo.features.filter((f) => cableSet.has(f.properties.id)) })
      const nodeSet = new Set(highlight.nodeIds ?? [])
      ;(map.getSource('hl-nodes') as GeoJSONSource).setData({ type: 'FeatureCollection', features: nodesGeo.features.filter((f) => nodeSet.has(f.properties.id)) })
      map.setPaintProperty('hl-cables', 'line-color', color)
      map.setPaintProperty('hl-cables', 'line-opacity', highlight.tone === 'danger' ? 0.85 : 0.5)
      map.setPaintProperty('hl-nodes', 'circle-stroke-color', color)
      map.setPaintProperty('hl-nodes', 'circle-stroke-width', highlight.tone === 'danger' ? 2.5 : 4)

      const p = highlight.point
      ;(map.getSource('pin') as GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: p ? [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: p.lngLat } }] : [],
      })
      if (p) {
        const r0 = metresToPixelsAtZoom0(p.radiusM, p.lngLat[1])
        map.setPaintProperty('pin-radius', 'circle-radius', ['interpolate', ['exponential', 2], ['zoom'], 0, r0, 22, r0 * 2 ** 22])
      }
    })
  }, [highlight, cablesGeo, nodesGeo])

  useEffect(() => {
    whenReady((map) => {
      const features =
        selection?.type === 'node'
          ? nodesGeo.features.filter((f) => f.properties.id === selection.id)
          : selection?.type === 'cable'
            ? cablesGeo.features.filter((f) => f.properties.id === selection.id)
            : []
      ;(map.getSource('selected') as GeoJSONSource).setData({ type: 'FeatureCollection', features } as Parameters<GeoJSONSource['setData']>[0])
    })
  }, [selection, cablesGeo, nodesGeo])

  // ---- geometry being drawn
  useEffect(() => {
    whenReady((map) => {
      const features: object[] = []
      if (draft?.line && draft.line.length > 1) features.push({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: draft.line } })
      for (const p of draft?.points ?? []) features.push({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: p } })
      ;(map.getSource('draft') as GeoJSONSource).setData({ type: 'FeatureCollection', features } as Parameters<GeoJSONSource['setData']>[0])
      map.getCanvas().style.cursor = interaction ? 'crosshair' : ''
    })
  }, [draft, interaction])

  // ---- external focus requests (search, OTDR result, "show on map")
  useEffect(() => {
    if (!focus) return
    fitted.current = true // an explicit focus wins over the first automatic fit
    whenReady((map) =>
      focus.bounds
        ? map.fitBounds(focus.bounds, { padding: fitPadding(map), maxZoom: 18, duration: 700 })
        : map.flyTo({ center: focus.lngLat, zoom: Math.max(map.getZoom(), focus.zoom ?? 17), duration: 700 }),
    )
  }, [focus])

  const btn = 'flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-surface text-fg shadow-panel hover:bg-surface-2'
  const locate = () => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => mapRef.current?.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 17 }),
      () => undefined,
      { enableHighAccuracy: true, timeout: 8000 },
    )
  }

  return (
    <div className="relative h-full w-full">
      <div ref={container} className="absolute inset-0" role="region" aria-label="Fibre network map. Use search or the lists in the side panel for keyboard access." />
      {interaction && (
        <div role="status" className="absolute left-1/2 top-3 z-10 flex w-max max-w-[92%] -translate-x-1/2 flex-wrap items-center justify-center gap-x-3 gap-y-1.5 rounded-lg bg-[#6d28d9] px-3 py-2 text-center text-sm font-medium text-white shadow-panel">
          <span>{interaction.hint}</span>
          {interaction.actions?.map((a) => (
            <button key={a.label} onClick={a.onClick} className="rounded-md bg-white px-2.5 py-1 text-xs font-semibold text-[#4c1d95] hover:bg-violet-100">
              {a.label}
            </button>
          ))}
        </div>
      )}
      <div className="absolute right-3 top-3 flex flex-col gap-2">
        <button className={btn} onClick={() => mapRef.current?.zoomIn()} aria-label="Zoom in" title="Zoom in">
          <Plus className="h-5 w-5" aria-hidden />
        </button>
        <button className={btn} onClick={() => mapRef.current?.zoomOut()} aria-label="Zoom out" title="Zoom out">
          <Minus className="h-5 w-5" aria-hidden />
        </button>
        <button className={btn} onClick={() => fit()} aria-label="Fit whole network" title="Fit whole network">
          <Maximize2 className="h-5 w-5" aria-hidden />
        </button>
        <button className={btn} onClick={locate} aria-label="Go to my location" title="Go to my location">
          <Crosshair className="h-5 w-5" aria-hidden />
        </button>
      </div>
    </div>
  )
}
